import type { IFinsquidBankIDLoginRequest, IFinsquidBankIDLoginResponse } from './types'
import { finsquid } from '.'
import { usePoll } from '@/utils/hooks/usePoll';
import { reactive } from 'vue';
import { sleep } from '@/utils/time';


export type UseBankIDAuthentication = {
  start: (props: {
    providerId: number,
    req: IFinsquidBankIDLoginRequest,
    callback?: (data: IFinsquidBankIDLoginResponse | null | undefined) => void
  }) => Promise<IFinsquidBankIDLoginResponse | undefined | null>;
  stop: () => void;
  state: {
    initRequest: IFinsquidBankIDLoginRequest | undefined
    initResponse: IFinsquidBankIDLoginResponse | undefined,
    status: IFinsquidBankIDLoginResponse | undefined,
    sid: string | undefined,
    initialized: boolean
  };
  clear: () => void;
  cancel: () => Promise<void>;
}

export const useBankIDAuthentication = (config: {
  handleError?: (e: unknown) => (void | Promise<void>)
  handleFail?: (status: IFinsquidBankIDLoginResponse) => (void | Promise<void>)
}): UseBankIDAuthentication => {

  const state = reactive<{
    initRequest: IFinsquidBankIDLoginRequest | undefined
    initResponse: IFinsquidBankIDLoginResponse | undefined,
    status: IFinsquidBankIDLoginResponse | undefined,
    sid: string | undefined,
    initialized: boolean
  }>({
    initRequest: undefined,
    initResponse: undefined,
    status: undefined,
    sid: undefined,
    initialized: false
  });

  const clear = () => {
    state.initRequest = undefined;
    state.initResponse = undefined;
    state.status = undefined;
    state.sid = undefined;
    state.initialized = false;
  }

  const poll = usePoll<IFinsquidBankIDLoginResponse>({
    fun: async ({ finish, update }) => {
      if (!state.initialized || !state.sid || !state.initRequest) {
        finish(null);
        return;
      }

      if (state.status?.status === 'complete') return;

      try {
        const status = await finsquid.getAuthBankIDStatus(state.initRequest.sameDevice);
        state.status = status;
          update(status);

        console.log({ status });

        if (status) {
          if (status.status === 'complete') {
            // This needs to be called, otherwise the session won't have a "user account" selected.
            //await finsquid.getUserAccounts();
            finish(status);
            poll.stop();
          } else if (status.status === 'failed' || status.status === 'timeout') {
            poll.stop();
            if (config.handleFail) {
              config.handleFail(status);
            }
            finish(null);
            return;
          } else if (status.status === 'error') {
            poll.stop()
            finish(status)
            return
          }
        }
      } catch (e) {
        console.error(e);
        if (config.handleError) {
          config.handleError(e);
        }
      }
    },
    handleError: async (e) => {
      if (config.handleError) {
        await config.handleError(e);
      }
    },
    delay: 500
  });

  const start = async (props: {
    providerId: number,
    req: IFinsquidBankIDLoginRequest,
    callback?: (data: IFinsquidBankIDLoginResponse | null | undefined) => void
  }) => {

    state.initRequest = props.req;
    clear();

    try {
      const resp = await finsquid.authBankIDInit(props.providerId, props.req);
      if (props.callback) {
        try {
          await props.callback(resp);
        } catch (e) {
          if (config.handleError) {
            await config.handleError(e);
          }
          throw e;
        }
      }
      await sleep(500);
      state.initResponse = resp;
      state.sid = resp.sid;
      state.initialized = true;
      state.initRequest = props.req;
      return await poll.start(props.callback);
    } catch (e) {
      poll.stop();
      if (config.handleError) {
        await config.handleError(e);
      }
    }
  }

  const cancel = async () => {
    poll.stop();
    if (state.sid) {
      try {
        await finsquid.cancelAuthBankId(state.sid)
      } catch (e) {
        console.warn(e);
      }
    };
    clear()
  }

  return { start, stop: poll.stop, state, clear, cancel };
}
